import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import {
  IconSync, IconRefresh, IconCheck, IconX,
  IconDatabase, IconCalendar, IconAlertTriangle,
} from "../components/icons";
import { fmtDateTime } from "../lib/format";

// ── Types matching the actual API responses ───────────────────────────────────

interface DataSource {
  id: string;
  name: string;
  connectionStatus: string;
  lastSyncAt: string | null;
  host: string | null;
  database: string | null;
  externalCompanyCodes: number[];
}

interface SyncJobRaw {
  id: string | null;
  entity: string;
  status: "running" | "completed" | "failed" | "never_synced";
  strategy: "full" | "incremental" | null;
  startedAt: string | null;
  finishedAt: string | null;
  rowsRead: number | null;
  rowsInserted: number | null;
  rowsUpdated: number | null;
  rowsSkipped: number | null;
  errorMessage: string | null;
  createdAt: string | null;
}

interface SyncJobHistoryRaw {
  id: string;
  entity: string;
  status: "running" | "completed" | "failed";
  startedAt: string;
  finishedAt: string | null;
  rowsRead: number | null;
  rowsInserted: number | null;
  rowsUpdated: number | null;
  errorMessage: string | null;
  logs: { id: string; level: string; message: string; createdAt: string }[];
}

// ── Helper to derive aggregate status from per-entity jobs ───────────────────
function deriveStatus(jobs: SyncJobRaw[]) {
  const synced = jobs.filter((j) => j.status !== "never_synced");
  if (!synced.length) return { isConnected: false, lastSyncAt: null, lastSyncStatus: null, entitiesSynced: 0, rowsProcessed: 0, errors: 0, nextScheduledAt: null };
  const latest = synced.reduce((a, b) => ((a.startedAt ?? "") > (b.startedAt ?? "") ? a : b));
  const totalRows = synced.reduce((s, j) => s + (j.rowsRead ?? 0), 0);
  const errCount = synced.filter((j) => j.status === "failed").length;
  return {
    isConnected: true,
    lastSyncAt: latest.startedAt,
    lastSyncStatus: errCount === synced.length ? "failed" : errCount > 0 ? "partial" : "success",
    entitiesSynced: synced.length,
    rowsProcessed: totalRows,
    errors: errCount,
    nextScheduledAt: null as string | null,
  };
}

// ── Trigger modal ─────────────────────────────────────────────────────────────
function TriggerModal({
  dataSources,
  onClose,
  onTrigger,
  loading,
}: {
  dataSources: DataSource[];
  onClose: () => void;
  onTrigger: (dsId: string, codes: number[], force: boolean) => void;
  loading: boolean;
}) {
  const [dsId, setDsId] = useState(dataSources[0]?.id ?? "");
  const [force, setForce] = useState(false);
  const [error, setError] = useState("");

  const selectedDs = dataSources.find((d) => d.id === dsId);
  const codes = selectedDs?.externalCompanyCodes ?? [];

  const submit = () => {
    if (!dsId) { setError("Selecione uma fonte de dados."); return; }
    if (codes.length === 0) { setError("A fonte de dados não tem códigos de empresa configurados."); return; }
    onTrigger(dsId, codes, force);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-card-lg w-full max-w-md mx-4 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Disparar Sincronização</h2>
          <button onClick={onClose} className="btn-ghost p-1">
            <IconX className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fonte de Dados</label>
            <select
              value={dsId}
              onChange={(e) => setDsId(e.target.value)}
              className="w-full border border-surface-border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-tm-500"
            >
              {dataSources.map((ds) => (
                <option key={ds.id} value={ds.id}>
                  {ds.name} — {ds.host}/{ds.database}
                </option>
              ))}
            </select>
          </div>

          {/* Códigos detectados automaticamente do configJson */}
          <div className="bg-surface-muted rounded-lg px-4 py-3 space-y-1">
            <p className="text-xs font-medium text-gray-600">Códigos de empresa que serão sincronizados</p>
            {codes.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {codes.map((c) => (
                  <span key={c} className="badge-gray font-mono">{c}</span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-warning-600">Nenhum código configurado nesta fonte de dados.</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              Todos os códigos são sincronizados em sequência em uma única operação.
            </p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              className="accent-tm-500 w-4 h-4"
            />
            <span className="text-sm text-gray-700">Forçar sincronização completa (ignora watermark)</span>
          </label>

          {error && (
            <div className="flex items-center gap-2 text-xs text-danger-600 bg-danger-50 border border-danger-200 rounded-lg px-3 py-2">
              <IconAlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={submit} disabled={loading || codes.length === 0} className="btn-primary flex-1">
            {loading ? (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <IconSync className="w-4 h-4" />
            )}
            {loading ? "Iniciando…" : `Sincronizar (${codes.length} código${codes.length !== 1 ? "s" : ""})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
// ── Telemetria GOBRAX ─────────────────────────────────────────────────────────

interface GobraxSyncStatus {
  enabled: boolean;
  cron: string;
  running: { trigger: "cron" | "manual"; startedAt: string } | null;
  lastRun: {
    trigger: "cron" | "manual";
    startedAt: string;
    finishedAt: string;
    ok: boolean;
    summary: string;
    result: { semCadastro: string[]; odometroRegressoes: string[] } | null;
  } | null;
  lastSyncedAt: string | null;
  lastReadingAt: string | null;
  platesWithOdometer: number;
}

/**
 * Coleta manual do odômetro da GOBRAX.
 *
 * O botão pede confirmação em dois passos porque a coleta é um LOGIN na conta
 * da GOBRAX do cliente, e há indício de sessão única por conta: quem estiver
 * logado lá pode ser desconectado. O cron roda de madrugada por isso; o
 * disparo manual é para quando o dado é necessário antes — e quem clica
 * precisa saber o que está fazendo.
 */
function GobraxCard() {
  const queryClient = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const { data: st } = useQuery<GobraxSyncStatus>({
    queryKey: ["gobrax", "sync-status"],
    queryFn: () => api.get("/gobrax/sync/status"),
    staleTime: 10_000,
    // Enquanto roda, acompanha de perto; parado, o status muda uma vez por dia.
    refetchInterval: (q) => (q.state.data?.running ? 4_000 : 60_000),
  });

  const disparar = async () => {
    setErro(null);
    setConfirmando(false);
    try {
      await api.post("/gobrax/sync/run");
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["gobrax", "sync-status"] }), 1_000);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao disparar a coleta.");
    }
  };

  if (!st) return null;

  const rodando = st.running != null;
  const ultimo = st.lastRun;

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${st.enabled ? "bg-tm-50 text-tm-600" : "bg-gray-100 text-gray-400"}`}>
          <IconSync className={`w-6 h-6 ${rodando ? "animate-spin" : ""}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900">Telemetria GOBRAX — odômetro</p>
            {!st.enabled && <span className="badge-gray">Desligada</span>}
            {st.enabled && rodando && <span className="badge-amber">Coletando…</span>}
            {st.enabled && !rodando && ultimo && (
              <span className={ultimo.ok ? "badge-green" : "badge-red"}>{ultimo.ok ? "Sucesso" : "Falhou"}</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {st.enabled
              ? `Automático às ${cronLabel(st.cron)} · não roda no deploy`
              : "Preencha GOBRAX_ENABLED, BASE_URL, USER e PASSWORD na stack para ligar."}
          </p>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs text-gray-600">
            <p><span className="text-gray-400">Última coleta:</span> {st.lastSyncedAt ? fmtDateTime(st.lastSyncedAt) : "nunca"}</p>
            <p><span className="text-gray-400">Leitura mais recente:</span> {st.lastReadingAt ? fmtDateTime(st.lastReadingAt) : "—"}</p>
            <p><span className="text-gray-400">Placas com odômetro:</span> {st.platesWithOdometer}</p>
          </div>
          {ultimo && (
            <p className={`text-xs mt-2 ${ultimo.ok ? "text-gray-500" : "text-danger-600"}`}>
              {ultimo.trigger === "manual" ? "Manual" : "Agendada"} em {fmtDateTime(ultimo.startedAt)}: {ultimo.summary}
            </p>
          )}
          {ultimo?.result && ultimo.result.semCadastro.length > 0 && (
            <p className="text-xs text-warning-700 mt-1">
              Fora do cadastro: {ultimo.result.semCadastro.join(", ")}
            </p>
          )}
          {ultimo?.result && ultimo.result.odometroRegressoes.length > 0 && (
            <p className="text-xs text-warning-700 mt-1">
              Odômetro andou para trás (não gravado): {ultimo.result.odometroRegressoes.join(", ")}
            </p>
          )}
          {erro && <p className="text-xs text-danger-600 mt-2">{erro}</p>}
        </div>
        <div className="flex-shrink-0">
          {!confirmando && (
            <button
              onClick={() => setConfirmando(true)}
              disabled={!st.enabled || rodando}
              className="btn-primary"
            >
              {rodando ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <IconRefresh className="w-4 h-4" />
              )}
              {rodando ? "Coletando…" : "Coletar agora"}
            </button>
          )}
        </div>
      </div>

      {confirmando && (
        <div className="flex items-start gap-3 bg-warning-50 border border-warning-200 text-warning-800 px-4 py-3 rounded-xl text-sm">
          <IconAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">A coleta faz login na conta da GOBRAX do cliente.</p>
            <p className="text-xs mt-1 text-warning-700">
              Se alguém estiver usando a GOBRAX agora, pode ser desconectado. Leva de 30 s a 2 min;
              o resultado aparece neste card. Prefira fora do expediente.
            </p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setConfirmando(false)} className="btn-secondary py-1 text-xs">Cancelar</button>
              <button onClick={disparar} className="btn-primary py-1 text-xs">Entendi, coletar agora</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** "30 3 * * *" → "03:30". Qualquer outra forma volta como está. */
function cronLabel(expr: string): string {
  const m = expr.trim().match(/^(\d{1,2}) (\d{1,2}) \* \* \*$/);
  if (!m) return expr;
  return `${m[2]!.padStart(2, "0")}:${m[1]!.padStart(2, "0")}`;
}

export function SyncPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Fetch data sources to pick the right one for triggering
  const { data: dsData } = useQuery<{ items: DataSource[] }>({
    queryKey: ["data-sources"],
    queryFn: () => api.get("/data-sources"),
    staleTime: 60_000,
  });

  // Per-entity status (latest job per entity)
  const { data: statusData } = useQuery<{ jobs: SyncJobRaw[] }>({
    queryKey: ["sync", "status"],
    queryFn: () => api.get("/sync/status"),
    staleTime: 30_000,
    refetchInterval: 15_000,
  });

  // Full job history
  const { data: jobsData, refetch: refetchJobs } = useQuery<{ jobs: SyncJobHistoryRaw[] }>({
    queryKey: ["sync", "jobs"],
    queryFn: () => api.get("/sync/jobs?limit=30"),
    staleTime: 30_000,
  });

  const dataSources = dsData?.items ?? [];
  const connectedDs = dataSources.filter((d) => d.connectionStatus === "ok");

  const entityJobs = statusData?.jobs ?? [];
  const historyJobs = jobsData?.jobs ?? [];
  const s = deriveStatus(entityJobs);

  const triggerSync = async (dsId: string, codes: number[], force: boolean) => {
    setSyncing(true);
    try {
      await api.post("/sync/trigger", {
        dataSourceId: dsId,
        externalCompanyCodes: codes,
        force,
      });
      setTimeout(() => {
        refetchJobs();
        queryClient.invalidateQueries({ queryKey: ["sync", "status"] });
      }, 2000);
    } catch (err) {
      console.error("[sync trigger]", err);
    } finally {
      setShowModal(false);
      setTimeout(() => setSyncing(false), 3000);
    }
  };

  const statusLabel = (st: string) => {
    if (st === "completed")    return "Sucesso";
    if (st === "failed")       return "Falhou";
    if (st === "never_synced") return "Aguardando";
    return "Rodando";
  };
  const statusBadge = (st: string) => {
    if (st === "completed")    return "badge-green";
    if (st === "failed")       return "badge-red";
    if (st === "never_synced") return "badge-gray";
    return "badge-amber";
  };

  return (
    <>
      {showModal && (
        <TriggerModal
          dataSources={connectedDs.length ? connectedDs : dataSources}
          onClose={() => setShowModal(false)}
          onTrigger={triggerSync}
          loading={syncing}
        />
      )}

      <div className="space-y-6">
        <PageHeader
          title="Sincronização"
          subtitle="Controle da integração com o banco de dados da Transmasi"
          actions={
            <>
              <button
                onClick={() => refetchJobs()}
                className="btn-secondary"
              >
                <IconRefresh className="w-4 h-4" />
                Atualizar
              </button>
              <button
                onClick={() => setShowModal(true)}
                disabled={syncing}
                className="btn-primary"
              >
                {syncing ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <IconRefresh className="w-4 h-4" />
                )}
                {syncing ? "Sincronizando…" : "Sincronizar agora"}
              </button>
            </>
          }
        />

        {/* No data sources warning */}
        {dataSources.length === 0 && (
          <div className="flex items-center gap-3 bg-warning-50 border border-warning-200 text-warning-700 px-4 py-3 rounded-xl text-sm">
            <IconAlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              Nenhum banco de dados configurado. Vá em{" "}
              <strong>Configurações → Bancos de Dados</strong> e adicione uma conexão primeiro.
            </span>
          </div>
        )}

        {/* Connection status */}
        <div className={`flex items-center gap-4 card p-5 ${s.isConnected ? "border-tm-200" : ""}`}>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.isConnected ? "bg-tm-50 text-tm-600" : "bg-gray-100 text-gray-400"}`}>
            <IconDatabase className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${s.isConnected ? "bg-tm-500 animate-pulse" : "bg-gray-300"}`} />
              <p className="text-sm font-semibold text-gray-900">
                {s.isConnected ? "Dados sincronizados" : "Sem histórico de sincronização"}
              </p>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {s.lastSyncAt
                ? `Última sincronização: ${fmtDateTime(s.lastSyncAt)}`
                : 'Nenhuma sincronização realizada — clique em "Sincronizar agora" para iniciar.'}
            </p>
          </div>
          <div className="text-right">
            {s.lastSyncStatus && (
              <span className={
                s.lastSyncStatus === "success" ? "badge-green"
                : s.lastSyncStatus === "failed" ? "badge-red"
                : s.lastSyncStatus === "partial" ? "badge-amber"
                : "badge-amber"
              }>
                {s.lastSyncStatus === "success" ? "Sucesso"
                  : s.lastSyncStatus === "failed" ? "Falhou"
                  : s.lastSyncStatus === "partial" ? "Parcial"
                  : "Rodando"}
              </span>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-2xl font-bold text-gray-900">{s.entitiesSynced}</p>
            <p className="text-sm text-gray-500 mt-0.5">Entidades sincronizadas</p>
          </div>
          <div className="card p-4">
            <p className="text-2xl font-bold text-gray-900">{s.rowsProcessed.toLocaleString("pt-BR")}</p>
            <p className="text-sm text-gray-500 mt-0.5">Linhas processadas</p>
          </div>
          <div className="card p-4">
            <p className={`text-2xl font-bold ${s.errors > 0 ? "text-danger-600" : "text-tm-700"}`}>{s.errors}</p>
            <p className="text-sm text-gray-500 mt-0.5">Entidades com erro</p>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <IconCalendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-800">Próximo sync</p>
              <p className="text-xs text-gray-400">A cada 30 min (automático)</p>
            </div>
          </div>
        </div>

        {/* Telemetria GOBRAX — coleta agendada + disparo manual */}
        <GobraxCard />

        {/* Per-entity status — always shown, even when nothing has been synced yet */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">Status por Entidade</p>
            <span className="text-xs text-gray-400">{entityJobs.length} entidades</span>
          </div>
          <div className="divide-y divide-surface-border">
            {entityJobs.map((job) => (
              <div key={job.entity} className="px-5 py-3 flex items-center gap-4">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs ${
                  job.status === "completed"    ? "bg-tm-50 text-tm-600"
                  : job.status === "failed"     ? "bg-danger-50 text-danger-600"
                  : job.status === "never_synced" ? "bg-gray-100 text-gray-400"
                  : "bg-warning-50 text-warning-600"
                }`}>
                  {job.status === "completed"     ? <IconCheck className="w-3.5 h-3.5" />
                  : job.status === "failed"        ? <IconX className="w-3.5 h-3.5" />
                  : job.status === "never_synced"  ? <IconDatabase className="w-3.5 h-3.5" />
                  : <IconSync className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{job.entity}</p>
                  {job.errorMessage && (
                    <p className="text-xs text-danger-600 mt-0.5 truncate">{job.errorMessage}</p>
                  )}
                  {job.status === "never_synced" && (
                    <p className="text-xs text-gray-400 mt-0.5">Nunca sincronizado — clique em "Sincronizar agora"</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={statusBadge(job.status)}>{statusLabel(job.status)}</span>
                  <span className="text-xs text-gray-400 hidden sm:block">
                    {job.rowsRead != null ? `${job.rowsRead.toLocaleString("pt-BR")} lidas` : "—"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Job history */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">Histórico de Jobs</p>
            <button onClick={() => refetchJobs()} className="btn-secondary py-1 text-xs">
              <IconRefresh className="w-3.5 h-3.5" />
              Atualizar
            </button>
          </div>
          {historyJobs.length === 0 ? (
            <EmptyState
              icon={<IconSync className="w-7 h-7" />}
              title="Sem histórico de jobs"
              description='Dispare a primeira sincronização clicando em "Sincronizar agora".'
              compact
            />
          ) : (
            <div className="divide-y divide-surface-border">
              {historyJobs.map((job) => (
                <div key={job.id} className="px-5 py-4 flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    job.status === "completed" ? "bg-tm-50 text-tm-600"
                    : job.status === "failed" ? "bg-danger-50 text-danger-600"
                    : "bg-warning-50 text-warning-600"
                  }`}>
                    {job.status === "completed" ? <IconCheck className="w-4 h-4" /> : job.status === "failed" ? <IconX className="w-4 h-4" /> : <IconSync className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-800">{job.entity}</p>
                      <span className="text-xs text-gray-400">{fmtDateTime(job.startedAt)}</span>
                      <span className={statusBadge(job.status)}>{statusLabel(job.status)}</span>
                    </div>
                    {job.errorMessage && (
                      <p className="text-xs text-danger-600 mt-0.5 truncate">{job.errorMessage}</p>
                    )}
                  </div>
                  <div className="text-right text-xs text-gray-400 flex-shrink-0 hidden sm:block">
                    {job.rowsInserted != null && <p>{job.rowsInserted.toLocaleString("pt-BR")} inseridas</p>}
                    {job.rowsUpdated != null && <p>{job.rowsUpdated.toLocaleString("pt-BR")} atualizadas</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
